import React, { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

// 2026 VA Compensation Rates (monthly) — same as disability calculator
const VA_RATES = {
  10: 175.51, 20: 347.14, 30: 537.94, 40: 775.15, 50: 1103.17,
  60: 1395.79, 70: 1759.04, 80: 2044.65, 90: 2298.11, 100: 3823.89,
};

// Historical rates for back pay calculation (simplified annual averages)
const HISTORICAL_RATES = {
  2023: { 10: 165.92, 20: 327.99, 30: 508.05, 40: 731.86, 50: 1041.82, 60: 1319.65, 70: 1663.06, 80: 1933.15, 90: 2172.39, 100: 3621.95 },
  2024: { 10: 171.23, 20: 338.49, 30: 524.31, 40: 755.28, 50: 1075.16, 60: 1361.88, 70: 1716.28, 80: 1995.01, 90: 2241.91, 100: 3737.85 },
  2025: { 10: 175.51, 20: 347.14, 30: 537.94, 40: 775.15, 50: 1103.17, 60: 1395.79, 70: 1759.04, 80: 2044.65, 90: 2298.11, 100: 3823.89 },
  2026: { 10: 175.51, 20: 347.14, 30: 537.94, 40: 775.15, 50: 1103.17, 60: 1395.79, 70: 1759.04, 80: 2044.65, 90: 2298.11, 100: 3823.89 },
};

const RATING_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

function monthsBetween(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
}

function getRateForDate(rating, date) {
  const year = new Date(date).getFullYear();
  const rates = HISTORICAL_RATES[Math.min(Math.max(year, 2023), 2026)];
  return rates[rating] || 0;
}

const BackPayCalculator = () => {
  const [rating, setRating] = useState(70);
  const [effectiveDate, setEffectiveDate] = useState('2024-01-15');
  const [decisionDate, setDecisionDate] = useState('2026-02-01');

  const results = useMemo(() => {
    if (!effectiveDate || !decisionDate) return null;

    const months = monthsBetween(effectiveDate, decisionDate);
    if (months <= 0) return { totalBackPay: 0, months: 0, monthlyRate: 0 };

    // Calculate using rate for effective date year
    let totalBackPay = 0;
    const startDate = new Date(effectiveDate);

    for (let i = 0; i < months; i++) {
      const currentDate = new Date(startDate);
      currentDate.setMonth(currentDate.getMonth() + i);
      const rate = getRateForDate(rating, currentDate);
      totalBackPay += rate;
    }

    const currentMonthlyRate = VA_RATES[rating] || 0;

    return {
      totalBackPay: Math.round(totalBackPay * 100) / 100,
      months,
      monthlyRate: currentMonthlyRate,
      effectiveYear: new Date(effectiveDate).getFullYear(),
    };
  }, [rating, effectiveDate, decisionDate]);

  return (
    <div className="space-y-6" data-testid="backpay-calculator">
      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-sm font-medium text-slate-700">VA Rating</Label>
          <select
            value={rating}
            onChange={(e) => setRating(Number(e.target.value))}
            className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="backpay-rating"
          >
            {RATING_OPTIONS.map(r => <option key={r} value={r}>{r}%</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Effective Date</Label>
          <Input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="mt-1"
            data-testid="effective-date"
          />
          <p className="text-[10px] text-slate-500 mt-1">Date VA assigns for your claim</p>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Decision Date</Label>
          <Input
            type="date"
            value={decisionDate}
            onChange={(e) => setDecisionDate(e.target.value)}
            className="mt-1"
            data-testid="decision-date"
          />
          <p className="text-[10px] text-slate-500 mt-1">Date VA granted your rating</p>
        </div>
      </div>

      {/* Results */}
      {results && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Months of Back Pay</p>
                <p className="text-3xl font-bold text-slate-800" data-testid="backpay-months">{results.months}</p>
                <p className="text-[10px] text-slate-400 mt-1">{effectiveDate} to {decisionDate}</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Current Monthly Rate</p>
                <p className="text-3xl font-bold text-green-700">${results.monthlyRate.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                <p className="text-[10px] text-slate-400 mt-1">At {rating}% rating</p>
              </div>
              <div className="text-center p-4 bg-amber-100 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700 mb-1 font-medium">Total Back Pay</p>
                <p className="text-3xl font-bold text-amber-800" data-testid="total-backpay">${results.totalBackPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                <p className="text-[10px] text-amber-600 mt-1">Tax-free lump sum</p>
              </div>
            </div>

            <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-100/50 p-2.5 rounded-lg">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Back pay includes COLA adjustments for each year. Filing an Intent to File (VA Form 21-0966) protects your effective date for up to 1 year. This estimate uses the veteran-alone rate.</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BackPayCalculator;
